import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ChevronDown, ChevronUp, Plus, Trash2, GripVertical } from 'lucide-react';

const DEFAULT_SECTIONS = [
  { title: '1. Who We Are', content: 'Grassroots Coaching ("we", "our", "us") is a team management platform designed for grassroots football coaches. We provide tools for squad management, match day operations, training planning, and team communication.' },
  { title: '2. Information We Collect', content: '2.1 Information You Provide\n\n\u2022 Account information: Name, email address, and password when you register\n\u2022 Profile information: Coaching experience, coaching levels, age groups, display name\n\u2022 Team information: Team name, club badge, team colours, player details (names, positions, stats)\n\u2022 Payment information: Processed securely by Stripe; we do not store your card details\n\u2022 Communications: Messages sent through our live chat feature\n\n2.2 Information Collected Automatically\n\n\u2022 Usage data: Pages visited, features used, button clicks, and time spent on sections\n\u2022 Device information: Browser type, screen size, and operating system\n\u2022 Scroll and interaction data: Scroll depth, section views for improving user experience' },
  { title: '3. How We Use Your Information', content: 'We use your information to:\n\n\u2022 Provide and maintain our team management services\n\u2022 Process subscriptions and payments through Stripe\n\u2022 Send important account notifications and updates via email\n\u2022 Personalise your coaching dashboard and team experience\n\u2022 Improve our platform based on usage patterns and feedback\n\u2022 Provide AI-powered live chat support\n\u2022 Prevent fraud and ensure platform security' },
  { title: '4. Cookies & Local Storage', content: 'We use browser local storage to enhance your experience. You can manage your cookie preferences at any time using the cookie settings button on our website. A complete list of all cookies and local storage items used is shown in the table below.\n\nAll items are classified as Essential and are required for the website to function correctly. We do not currently deploy third-party analytics or marketing tracking cookies. Should this change in the future, we will update this policy and request your consent via the cookie banner.' },
  { title: '5. Third-Party Services', content: 'We use the following trusted third-party services to operate our platform:\n\n\u2022 Stripe \u2014 Payment processing for subscriptions. Stripe processes your payment information directly and securely. We never see or store your full card details.\n\u2022 OpenAI \u2014 Powers our AI live chat assistant. Chat messages are sent to OpenAI\'s API for processing. No personal account data is shared.\n\u2022 Resend \u2014 Email delivery service for account notifications, password resets, and team communications.' },
  { title: '6. Data Security', content: 'We take the security of your data seriously. Measures we employ include:\n\n\u2022 Passwords are hashed using industry-standard bcrypt encryption\n\u2022 All data is transmitted over HTTPS with TLS encryption\n\u2022 Authentication tokens expire and require periodic re-authentication\n\u2022 Payment data is handled entirely by PCI-DSS compliant Stripe\n\u2022 Access to admin functions is restricted by role-based permissions' },
  { title: '7. Data Retention', content: 'We retain your personal data for as long as your account is active or as needed to provide our services. If you delete your account, we will remove your personal information within 30 days, except where we are required to retain certain data for legal or regulatory purposes. Onboarding progress saved in local storage is automatically cleared upon successful registration.' },
  { title: '8. Your Rights', content: 'Depending on your location, you may have the following rights regarding your personal data:\n\n\u2022 Access: Request a copy of the personal data we hold about you\n\u2022 Correction: Request correction of inaccurate or incomplete data\n\u2022 Deletion: Request deletion of your personal data\n\u2022 Portability: Request your data in a portable, machine-readable format\n\u2022 Objection: Object to processing of your data for certain purposes\n\u2022 Withdraw consent: Withdraw consent for cookie preferences at any time via the cookie settings button' },
  { title: '9. Children\'s Privacy', content: 'Our platform is intended for use by coaches aged 18 and over. While coaches may manage teams that include players under 18, we do not knowingly collect personal information directly from children. Player data entered by coaches is the responsibility of the coach and should be managed in accordance with applicable data protection laws and with appropriate parental consent where required.' },
  { title: '10. Changes to This Policy', content: 'We may update this privacy policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date above. We encourage you to review this policy periodically.' },
  { title: '11. Contact Us', content: 'If you have any questions about this Privacy Policy, your data, or wish to exercise any of your rights, please contact us at the email address provided below.' },
];

const DEFAULT_COOKIES = [
  { name: 'token', type: 'Local Storage', purpose: 'Stores your authentication session token so you remain logged in between visits', category: 'Essential' },
  { name: 'cookie_consent', type: 'Local Storage', purpose: 'Stores your cookie consent preferences (essential, analytics, marketing choices)', category: 'Essential' },
  { name: 'cookie_consent_time', type: 'Local Storage', purpose: 'Records the timestamp of when you last set your cookie preferences, used to determine when to re-prompt', category: 'Essential' },
  { name: 'landingTheme', type: 'Local Storage', purpose: 'Remembers your preferred colour theme (dark or light) across the website', category: 'Essential' },
  { name: 'gc_onboarding_progress', type: 'Local Storage', purpose: 'Saves your onboarding form progress so you can resume if you leave and return before completing registration', category: 'Essential' },
  { name: 'chatSessionId', type: 'Local Storage', purpose: 'Maintains your live chat conversation session so message history persists during your visit', category: 'Essential' },
  { name: 'matchday_active_match_id', type: 'Local Storage', purpose: 'Tracks the currently active match day so the correct match loads when you return to the match day screen', category: 'Essential' },
];

export function PrivacyPolicyTab({ content, setContent }) {
  const pp = content.privacy_policy || {};
  const sections = pp.sections || DEFAULT_SECTIONS;
  const cookies = pp.cookies || DEFAULT_COOKIES;
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedCookie, setExpandedCookie] = useState(null);

  const update = (field, value) => {
    setContent(prev => ({
      ...prev,
      privacy_policy: { ...prev.privacy_policy, [field]: value },
    }));
  };

  const updateSection = (index, field, value) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], [field]: value };
    update('sections', updated);
  };

  const addSection = () => {
    const num = sections.length + 1;
    update('sections', [...sections, { title: `${num}. New Section`, content: '' }]);
    setExpandedSection(sections.length);
  };

  const removeSection = (index) => {
    update('sections', sections.filter((_, i) => i !== index));
  };

  const moveSection = (index, dir) => {
    const updated = [...sections];
    const target = index + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    update('sections', updated);
    setExpandedSection(target);
  };

  const updateCookie = (index, field, value) => {
    const updated = [...cookies];
    updated[index] = { ...updated[index], [field]: value };
    update('cookies', updated);
  };

  const addCookie = () => {
    update('cookies', [...cookies, { name: '', type: 'Local Storage', purpose: '', category: 'Essential' }]);
    setExpandedCookie(cookies.length);
  };

  const removeCookie = (index) => {
    update('cookies', cookies.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      {/* Page Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Privacy Policy</CardTitle>
          <CardDescription>Edit the privacy policy page content shown at /privacy-policy</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Page Heading</Label>
            <Input value={pp.heading || ''} onChange={(e) => update('heading', e.target.value)} placeholder="Privacy Policy" />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input value={pp.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} placeholder="Your privacy is important to us..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Last Updated</Label>
              <Input value={pp.last_updated || ''} onChange={(e) => update('last_updated', e.target.value)} placeholder="April 2026" />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input value={pp.contact_email || ''} onChange={(e) => update('contact_email', e.target.value)} placeholder="privacy@grassrootscoaching.app" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader>
          <CardTitle>Policy Sections</CardTitle>
          <CardDescription>Edit, reorder, add or remove sections. Use bullet points with \u2022 at the start of a line. Subheadings on their own line will be rendered bold.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sections.map((section, index) => (
            <div key={index} className="border rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-2 px-4 py-3 bg-gray-50 cursor-pointer"
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
              >
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="flex-1 font-medium text-sm truncate">{section.title || 'Untitled Section'}</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); moveSection(index, -1); }} disabled={index === 0}>
                    <ChevronUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); moveSection(index, 1); }} disabled={index === sections.length - 1}>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); removeSection(index); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              {expandedSection === index && (
                <div className="px-4 py-4 space-y-3 border-t">
                  <div>
                    <Label>Section Title</Label>
                    <Input value={section.title} onChange={(e) => updateSection(index, 'title', e.target.value)} />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <Textarea
                      value={section.content}
                      onChange={(e) => updateSection(index, 'content', e.target.value)}
                      rows={10}
                      className="font-mono text-sm"
                      placeholder="Section content..."
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full" onClick={addSection}>
            <Plus className="w-4 h-4 mr-2" /> Add Section
          </Button>
        </CardContent>
      </Card>

      {/* Cookies Table */}
      <Card>
        <CardHeader>
          <CardTitle>Cookie / Storage Items</CardTitle>
          <CardDescription>Manage the list of cookies and local storage items displayed in the privacy policy</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {cookies.map((cookie, index) => (
            <div key={index} className="border rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-2 px-4 py-3 bg-gray-50 cursor-pointer"
                onClick={() => setExpandedCookie(expandedCookie === index ? null : index)}
              >
                <code className="text-xs bg-gray-200 px-2 py-0.5 rounded">{cookie.name || 'unnamed'}</code>
                <span className="flex-1 text-sm text-gray-500 truncate">{cookie.purpose}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); removeCookie(index); }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {expandedCookie === index && (
                <div className="px-4 py-4 space-y-3 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Name</Label>
                      <Input value={cookie.name} onChange={(e) => updateCookie(index, 'name', e.target.value)} placeholder="cookie_name" className="font-mono text-sm" />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Input value={cookie.type} onChange={(e) => updateCookie(index, 'type', e.target.value)} placeholder="Local Storage" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input value={cookie.category} onChange={(e) => updateCookie(index, 'category', e.target.value)} placeholder="Essential" />
                    </div>
                  </div>
                  <div>
                    <Label>Purpose</Label>
                    <Textarea value={cookie.purpose} onChange={(e) => updateCookie(index, 'purpose', e.target.value)} rows={2} placeholder="Describe what this cookie is used for..." />
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full" onClick={addCookie}>
            <Plus className="w-4 h-4 mr-2" /> Add Cookie / Storage Item
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
